// src/services/communicationService.js
import { 
    collection, 
    doc, 
    addDoc, 
    getDoc, 
    getDocs, 
    updateDoc, 
    query, 
    where, 
    orderBy, 
    limit,
    startAfter,
    serverTimestamp, 
    writeBatch
  } from 'firebase/firestore';
  import { db } from './firebase';
  
  /**
   * Get all chat threads for a vendor
   * @param {string} vendorId - Vendor ID
   * @param {Object} options - Query options
   * @returns {Array} - Chat threads
   */
  export const getChatThreads = async (vendorId, options = {}) => {
    try {
      const {
        pageSize = 20,
        lastVisible = null,
        customerId = null
      } = options;
      
      // Create base query
      const chatsRef = collection(db, 'chats');
      let q = query(
        chatsRef,
        where('vendorId', '==', vendorId),
        orderBy('updatedAt', 'desc')
      );
      
      // Apply customer filter
      if (customerId) {
        q = query(q, where('customerId', '==', customerId));
      }
      
      // Apply pagination
      if (lastVisible) {
        q = query(q, startAfter(lastVisible), limit(pageSize));
      } else {
        q = query(q, limit(pageSize));
      }
      
      // Execute query
      const querySnapshot = await getDocs(q);
      
      // Process threads
      const threads = [];
      querySnapshot.forEach((doc) => {
        threads.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Get last visible document for pagination
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      
      return { threads, lastDoc };
    } catch (error) {
      console.error('Error getting chat threads:', error);
      throw error;
    }
  };
  
  /**
   * Get a single chat thread by ID
   * @param {string} threadId - Chat thread ID
   * @returns {Object} - Chat thread
   */
  export const getChatThreadById = async (threadId) => {
    try {
      const threadRef = doc(db, 'chats', threadId);
      const threadSnap = await getDoc(threadRef);
      
      if (threadSnap.exists()) {
        return {
          id: threadSnap.id,
          ...threadSnap.data()
        };
      } else {
        throw new Error('Chat thread not found');
      }
    } catch (error) {
      console.error('Error getting chat thread:', error);
      throw error;
    }
  };
  
  /**
   * Get messages for a chat thread
   * @param {string} threadId - Chat thread ID
   * @param {Object} options - Query options
   * @returns {Object} - { messages, lastDoc }
   */
  export const getChatMessages = async (threadId, options = {}) => {
    try {
      const {
        pageSize = 50,
        lastVisible = null
      } = options;
      
      // Create base query
      const messagesRef = collection(db, `chats/${threadId}/messages`);
      let q = query(
        messagesRef,
        orderBy('timestamp', 'desc')
      );
      
      // Apply pagination
      if (lastVisible) {
        q = query(q, startAfter(lastVisible), limit(pageSize));
      } else {
        q = query(q, limit(pageSize));
      }
      
      // Execute query
      const querySnapshot = await getDocs(q);
      
      // Process messages
      const messages = [];
      querySnapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Get last visible document for pagination
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      
      // Reverse messages to show in chronological order
      messages.reverse();
      
      return { messages, lastDoc };
    } catch (error) {
      console.error('Error getting chat messages:', error);
      throw error;
    }
  };
  
  /**
   * Send a message in a chat thread
   * @param {string} threadId - Chat thread ID
   * @param {Object} message - Message data
   * @returns {Object} - Sent message
   */
  export const sendMessage = async (threadId, message) => {
    try {
      // Add message to thread
      const messagesRef = collection(db, `chats/${threadId}/messages`);
      const messageData = {
        ...message,
        timestamp: new Date()
      };
      
      const messageDoc = await addDoc(messagesRef, messageData);
      
      // Update thread with last message
      const threadRef = doc(db, 'chats', threadId);
      await updateDoc(threadRef, {
        lastMessage: messageData.text,
        lastMessageSender: messageData.senderId,
        lastMessageTimestamp: new Date(),
        updatedAt: new Date()
      });
      
      // Mark as unread for recipient
      if (messageData.senderType === 'vendor') {
        await updateDoc(threadRef, { unreadCustomer: true });
      } else {
        await updateDoc(threadRef, { unreadVendor: true });
      }
      
      return {
        id: messageDoc.id,
        ...messageData
      };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };
  
  /**
   * Create a new chat thread
   * @param {Object} threadData - Thread data
   * @returns {Object} - Created thread
   */
  export const createChatThread = async (threadData) => {
    try {
      // Add thread
      const chatsRef = collection(db, 'chats');
      const thread = {
        ...threadData,
        createdAt: new Date(),
        updatedAt: new Date(),
        unreadVendor: false,
        unreadCustomer: true
      };
      
      const threadDoc = await addDoc(chatsRef, thread);
      
      return {
        id: threadDoc.id,
        ...thread
      };
    } catch (error) {
      console.error('Error creating chat thread:', error);
      throw error;
    }
  };
  
  /**
   * Mark a chat thread as read
   * @param {string} threadId - Chat thread ID
   * @param {string} userType - 'vendor' or 'customer'
   * @returns {boolean} - Success
   */
  export const markThreadAsRead = async (threadId, userType) => {
    try {
      const threadRef = doc(db, 'chats', threadId);
      
      if (userType === 'vendor') {
        await updateDoc(threadRef, { unreadVendor: false });
      } else {
        await updateDoc(threadRef, { unreadCustomer: false });
      }
      
      return true;
    } catch (error) {
      console.error('Error marking thread as read:', error);
      throw error;
    }
  };
  
  /**
   * Get unread count for a vendor
   * @param {string} vendorId - Vendor ID
   * @returns {number} - Unread count
   */
  export const getUnreadCount = async (vendorId) => {
    try {
      const chatsRef = collection(db, 'chats');
      const q = query(
        chatsRef,
        where('vendorId', '==', vendorId),
        where('unreadVendor', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting unread count:', error);
      throw error;
    }
  };
  
  /**
   * Send a message to all customers
   * @param {string} vendorId - Vendor ID
   * @param {string} message - Message text
   * @param {Array} customerIds - Optional array of customer IDs to send to
   * @returns {boolean} - Success
   */
  export const sendBroadcastMessage = async (vendorId, message, customerIds = null) => {
    try {
      // Get all threads for this vendor
      const chatsRef = collection(db, 'chats');
      let q = query(
        chatsRef,
        where('vendorId', '==', vendorId)
      );
      
      // Filter by specific customers if provided
      if (customerIds && customerIds.length > 0) {
        q = query(q, where('customerId', 'in', customerIds));
      }
      
      const querySnapshot = await getDocs(q);
      
      // Create batch operation
      const batch = writeBatch(db);
      
      // Prepare message data
      const messageData = {
        text: message,
        senderId: vendorId,
        senderType: 'vendor',
        timestamp: new Date(),
        isBroadcast: true
      };
      
      // Add messages and update threads
      querySnapshot.forEach((doc) => {
        const threadId = doc.id;
        
        // Add message
        const messagesRef = doc.ref.collection('messages').doc();
        batch.set(messagesRef, messageData);
        
        // Update thread
        batch.update(doc.ref, {
          lastMessage: message,
          lastMessageSender: vendorId,
          lastMessageTimestamp: new Date(),
          updatedAt: new Date(),
          unreadCustomer: true
        });
      });
      
      // Commit the batch
      await batch.commit();
      
      return true;
    } catch (error) {
      console.error('Error sending broadcast message:', error);
      throw error;
    }
  };
  
  // Export functions
  export default {
    getChatThreads,
    getChatThreadById,
    getChatMessages,
    sendMessage,
    createChatThread,
    markThreadAsRead,
    getUnreadCount,
    sendBroadcastMessage
  };